'use client';
import React, { FC, useState } from 'react';
import { Entry } from 'contentful';
import { DynamicComponent } from '@/components/common/DynamicComponent';
import { ChevronIcon } from '@/components';
import {
  captureTabNavigationEvent,
  NAVIGATION_EVENT_TYPES,
} from '@/utils/dataLayer';

export enum ExpandedTabDisplayTypes {
  DynamicComponents = 'DynamicComponents',
  JSX = 'JSX',
}

export enum NavigationDisplayTypes {
  FullWidth = 'FullWidth',
  Single = 'Single',
}
type TabRepresentation = {
  title: string;
  components?: Entry<Record<string, any>>[];
  jsxContent?: React.ReactNode;
  onSelectTab?: () => void;
};

export type TabsProps = {
  tabs: TabRepresentation[];
  expandedTabDisplayType: ExpandedTabDisplayTypes;
  navigationDisplayType: NavigationDisplayTypes;
  shouldUseActiveTabIndexOverride?: boolean;
  activeTabIndexOverride?: number;
  setActiveTabIndexOverride?: (idx: number) => void;
  enableInfiniteScroll?: boolean;
};

type SingleTab = {
  tab: TabRepresentation;
  index: number;
};

const Tabs: FC<TabsProps> = ({
  tabs,
  expandedTabDisplayType,
  navigationDisplayType,
  shouldUseActiveTabIndexOverride = false,
  activeTabIndexOverride,
  setActiveTabIndexOverride,
  enableInfiniteScroll,
}) => {
  const [activeTabIndexLocal, setActiveTabIndexLocal] = useState(0);

  const isSafeToUseActiveTabOverride =
    shouldUseActiveTabIndexOverride &&
    activeTabIndexOverride !== undefined &&
    tabs[activeTabIndexOverride];

  const activeTabIndex = isSafeToUseActiveTabOverride
    ? activeTabIndexOverride
    : activeTabIndexLocal;

  const activeTabComponents = tabs[activeTabIndex]
    ? tabs[activeTabIndex].components
    : [];

  const activeTabJsxContent = tabs[activeTabIndex]
    ? tabs[activeTabIndex].jsxContent
    : [];

  const hasNextTab = activeTabIndex < tabs.length - 1;
  const hasPreviousTab = activeTabIndex > 0;

  function handleTabSelection(index: number) {
    const customOnClick = tabs[index]?.onSelectTab;
    if (customOnClick) {
      customOnClick();
    }

    if (shouldUseActiveTabIndexOverride && setActiveTabIndexOverride) {
      setActiveTabIndexOverride(index);
    } else {
      setActiveTabIndexLocal(index);
    }
  }

  function selectNextTab() {
    // supports infinite scroll
    const nextTabIndex = hasNextTab ? activeTabIndex + 1 : 0;

    captureTabNavigationEvent(NAVIGATION_EVENT_TYPES.TAB_SEEK_FORWARD, {
      navigatedToTabPosition: nextTabIndex + 1, // convert from 0 to 1 based positions
      navigatedToTabTitle: tabs[nextTabIndex].title,
      tabsLength: tabs.length,
    });

    handleTabSelection(nextTabIndex);
  }

  function selectPreviousTab() {
    // supports infinite scroll
    const nextTabIndex = hasPreviousTab ? activeTabIndex - 1 : tabs.length - 1;

    captureTabNavigationEvent(NAVIGATION_EVENT_TYPES.TAB_SEEK_BACKWARD, {
      navigatedToTabPosition: nextTabIndex + 1, // convert from 0 to 1 based positions
      navigatedToTabTitle: tabs[nextTabIndex].title,
      tabsLength: tabs.length,
    });

    handleTabSelection(nextTabIndex);
  }

  const ARROW_SHARED_CLASSES =
    'md:flex-auto md:shrink sm:flex-0 sm:px-3 border-b-4 border-grey-midtone text-center disabled:cursor-not-allowed disabled:opacity-50 pb-2';
  const NextArrow = () => {
    const disabled = !enableInfiniteScroll && !hasNextTab;

    return (
      <button
        className={ARROW_SHARED_CLASSES}
        disabled={disabled}
        onClick={selectNextTab}
        data-testid="tab-selector-next-arrow"
      >
        <ChevronIcon direction="right" classes="ml-auto block" />
      </button>
    );
  };

  const PreviousArrow = () => {
    const disabled = !enableInfiniteScroll && !hasPreviousTab;

    return (
      <button
        className={ARROW_SHARED_CLASSES}
        disabled={disabled}
        onClick={selectPreviousTab}
        data-testid="tab-selector-previous-arrow"
      >
        <ChevronIcon direction="left" classes="mr-auto block" />
      </button>
    );
  };

  const SingleTab: FC<SingleTab> = ({ tab, index }) => {
    if (index !== activeTabIndex) return null;

    return (
      <p
        key={tab.title}
        onClick={() => handleTabSelection(index)}
        className="flex-auto select-none self-end border-b-4 border-indigo pb-2 text-center text-xl font-bold text-indigo"
      >
        {tabs[index].title}
      </p>
    );
  };

  return (
    <section>
      {navigationDisplayType === 'FullWidth' && (
        <div
          className={`tabs grid pt-2 grid-cols-${tabs.length}`}
          data-testid="tabs"
        >
          {tabs.map(({ title }, index) => (
            <div
              key={title}
              className={`tab mb-8 h-fit border-b-4 pb-3 text-h-x5 font-bold text-charcoal md:text-h-x4 ${
                index === activeTabIndex
                  ? 'border-indigo'
                  : 'border-indigo-pastel'
              }`}
              onClick={() => handleTabSelection(index)}
            >
              {title}
            </div>
          ))}
        </div>
      )}

      {navigationDisplayType === 'Single' && (
        <div className="mb-8 flex" data-testid="tabs">
          <PreviousArrow />
          {tabs.map((tab, index) => (
            <SingleTab key={tab.title} tab={tab} index={index} />
          ))}
          <NextArrow />
        </div>
      )}

      {expandedTabDisplayType === 'DynamicComponents' && (
        <div className={`md:grid md:grid-cols-2`} data-testid="tabs">
          {activeTabComponents?.map((component, i) => (
            <div key={`${component.sys.id}-${i}`} className="m-3">
              <DynamicComponent component={component} />
            </div>
          ))}
        </div>
      )}

      {expandedTabDisplayType === 'JSX' && <>{activeTabJsxContent}</>}
    </section>
  );
};

export default React.memo(Tabs);
